// Copyright (c) Ping. All rights reserved.
//
// Push notification deeplink: `/wake/{delivery_id}` (32 hex).
// Same opaque id as the push payload — no conversation_id on the wire
// (ping-prime-architecture.md §13.1).

import type { PushWakePayload } from './pushWake';

/** Matches message-service `parse_delivery_id` hex form. */
export const DELIVERY_ID_HEX_RE = /^[0-9a-fA-F]{32}$/;

const PENDING_WAKE_KEY = 'ping.pendingPushWake';

export function isValidDeliveryId(deliveryId: string): boolean {
  return DELIVERY_ID_HEX_RE.test(deliveryId.trim());
}

export function buildPushWakePath(deliveryId: string): string {
  return `/wake/${deliveryId.trim().toLowerCase()}`;
}

/**
 * Parses `/wake/{delivery_id}` (optional trailing segments ignored).
 */
export function parsePushWakePath(pathname: string): PushWakePayload | null {
  if (!pathname.startsWith('/wake')) {
    return null;
  }
  const rest = pathname.slice('/wake'.length);
  if (rest.length === 0 || rest === '/') {
    return null;
  }
  if (rest.charAt(0) !== '/') {
    return null;
  }
  const firstSlash = rest.indexOf('/', 1);
  const segment = firstSlash === -1 ? rest.slice(1) : rest.slice(1, firstSlash);
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    return null;
  }
  if (!isValidDeliveryId(decoded)) {
    return null;
  }
  return { delivery_id: decoded.toLowerCase() };
}

export function stashPendingPushWake(payload: PushWakePayload): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(PENDING_WAKE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / quota — best effort.
  }
}

export function takePendingPushWake(): PushWakePayload | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(PENDING_WAKE_KEY);
    if (raw === null) {
      return null;
    }
    sessionStorage.removeItem(PENDING_WAKE_KEY);
    const parsed = JSON.parse(raw) as PushWakePayload;
    const id = parsed.delivery_id?.trim();
    if (id === undefined || !isValidDeliveryId(id)) {
      return null;
    }
    return {
      delivery_id: id.toLowerCase(),
      ...(parsed.kind !== undefined ? { kind: parsed.kind } : {}),
      ...(parsed.is_video === true ? { is_video: true } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * When the document loaded on `/wake/{id}`, stash the payload and
 * replace the URL with `/chat` so the outer router stays valid.
 */
export function consumePushWakeUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const payload = parsePushWakePath(window.location.pathname);
  if (payload === null) {
    return false;
  }
  stashPendingPushWake(payload);
  window.history.replaceState({ route: '/chat' }, '', '/chat');
  return true;
}
